import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import MasterAset from "./MasterAset";
import MasterBarang from "./MasterBarang";

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeMenu, setActiveMenu] = useState(null);
  const [greeting, setGreeting] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userAccess, setUserAccess] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);

  
useEffect(() => {
  // simpan semua path dashboard, contoh: /dashboard/master-barang
  if (location.pathname?.startsWith("/dashboard")) {
    localStorage.setItem("lastDashboardPath", location.pathname);
  }
}, [location.pathname]);

  // ✅ Ambil akses user dari localStorage
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser?.access) setUserAccess(storedUser.access);
  }, []);

  // ✅ Handle resize (ubah mode mobile/desktop)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
      if (window.innerWidth >= 992) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Update menu aktif dari URL
  useEffect(() => {
    const pathParts = location.pathname.split("/");
    setActiveMenu(pathParts[2] || null);
  }, [location.pathname]);

  // ✅ Greeting waktu — sesuai pembagian jam baru
  useEffect(() => {
    const updateGreeting = () => {
      const now = new Date();
      const hour = now.getHours();
      const minute = now.getMinutes();
      const totalMinutes = hour * 60 + minute;

      if (totalMinutes >= 1 && totalMinutes <= 600) {
        setGreeting("🌅 Selamat Pagi");
      } else if (totalMinutes > 600 && totalMinutes <= 900) {
        setGreeting("🌤️ Selamat Siang");
      } else if (totalMinutes > 900 && totalMinutes <= 1080) {
        setGreeting("🌇 Selamat Sore");
      } else {
        setGreeting("🌙 Selamat Malam");
      }
    };

    updateGreeting();
    const timer = setInterval(updateGreeting, 60000); // update tiap 1 menit
    return () => clearInterval(timer);
  }, []);

  // ✅ Logout
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("selectedCabang");
    localStorage.removeItem("selectedGroup");
    localStorage.removeItem("selectedCategory");
    localStorage.removeItem("searchTerm");
    navigate("/");
  };

  const handleSetActiveMenu = (menuKey) => {
    setActiveMenu(menuKey);
    navigate(`/dashboard/${menuKey}`);
    setSidebarOpen(false);
  };

  // ✅ Efek shadow saat scroll konten
  useEffect(() => {
    const content = document.querySelector(".dashboard-content");
    if (!content) return;
    const handleScroll = () => setIsScrolled(content.scrollTop > 10);
    content.addEventListener("scroll", handleScroll);
    return () => content.removeEventListener("scroll", handleScroll);
  }, []);

  const pageVariants = {
    initial: { opacity: 0, x: 40 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: -40 },
  };

  const pageTransition = {
    type: "tween",
    ease: "easeInOut",
    duration: 0.35,
  };

  // 🔧 Lebar sidebar menyesuaikan perangkat
  const sidebarWidth = isMobile ? 220 : 280;

  return (
    <div
      className="dashboard-root"
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        background: "#f9fafb",
        overflow: "hidden",
      }}
    >
      {/* ✅ Navbar fixed */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: 64,
          background: "#fff",
          zIndex: 1000,
          boxShadow: isScrolled
            ? "0 2px 8px rgba(0,0,0,0.1)"
            : "0 1px 2px rgba(0,0,0,0.05)",
          transition: "box-shadow 0.3s ease",
        }}
      >
        <Navbar
          user={user}
          onLogout={handleLogout}
          onSelectMenu={handleSetActiveMenu}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onGoHome={() => navigate("/dashboard")}
          isMobile={isMobile}
        />
      </div>

      {/* ✅ Layout utama */}
      <div
        style={{
          display: "flex",
          flex: 1,
          marginTop: 64,
          height: "calc(100vh - 64px)",
          overflow: "hidden",
          position: "relative",
          background: "#f9fafb",
        }}
      >
        {/* ✅ Sidebar */}
        <Sidebar
          activeMenu={activeMenu}
          setActiveMenu={handleSetActiveMenu}
          visible={isMobile ? sidebarOpen : true}
          onClose={() => setSidebarOpen(false)}
          userAccess={userAccess}
        />

        {/* ✅ Overlay hitam transparan saat sidebar terbuka di mobile */}
        {isMobile && sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: "fixed",
              top: 64,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 150,
              transition: "opacity 0.3s ease",
            }}
          ></div>
        )}

        {/* ✅ Konten utama */}
        <div
          className="dashboard-content"
          style={{
            flex: 1,
            marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
            height: "calc(100vh - 64px)",
            overflowY: "auto",
            background:
              activeMenu === "master-barang" ? "transparent" : "#f9fafb",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            padding: isMobile ? "16px" : "24px",
            transition: "margin-left 0.3s ease",
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}
              style={{
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {!activeMenu && (
                <div
                  style={{
                    padding: "20px",
                    color: "#444",
                    fontSize: "1.1rem",
                  }}
                >
                  Halo, {user?.name || "User"} 👋 — {greeting}
                </div>
              )}

              <Routes location={location} key={location.pathname}>
                <Route path="master-aset" element={<MasterAset />} />
                <Route
                  path="master-barang"
                  element={<MasterBarang userAccess={userAccess} />}
                />
                <Route path="add-asset" element={<div>-</div>} />
                <Route path="stok-persediaan" element={<div></div>} />
                <Route path="permintaan-barang" element={<div></div>} />
                <Route path="purchase-request" element={<div></div>} />
                <Route path="purchase-order" element={<div></div>} />
                <Route path="pengajuan-rop" element={<div></div>} />
                <Route path="daftar-rop" element={<div></div>} />
                <Route path="*" element={<div></div>} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
